import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/config';
import { userPictureUrl } from '@/lib/media';
import ReportModal from './ReportModal';
import ModelCardPlaceholder from './ModelCardPlaceholder';
import ImageCropper from './ImageCropper';

// Perfil público e privado. Mostra biografia, seguidores, publicações,
// curtidos, histórico e coleções; o dono também pode editar foto e dados.
function ProfileModal({ userId, currentUser, onClose, authFetch, getMediaUrl, onPostClick, isOwn, onProfileUpdated, onOpenSettings, onOpenUser }) {
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editHandle, setEditHandle] = useState('');
  const [saving, setSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [following, setFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');
  const [people, setPeople] = useState([]);
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [reportOpen,setReportOpen]=useState(false);
  const [cropRequest,setCropRequest]=useState(null);

  useEffect(() => {
    loadProfile();
    setActiveTab('posts');
  }, [userId]);

  useEffect(() => { loadTab(); }, [activeTab,userId]);

  const loadProfile = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/users/${userId}`);
      if (!response.ok) throw new Error(`Profile request failed: ${response.status}`);
      const data = await response.json();
      setProfile(data);
      setEditName(data.name);
      setEditBio(data.bio || '');
      setEditHandle(data.handle || '');
      setFollowing(data.is_following);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadTab = async () => {
    try {
      let endpoint=`/api/users/${userId}/posts?limit=30`;
      if(activeTab==='followers'||activeTab==='following') endpoint=`/api/users/${userId}/${activeTab}`;
      if(activeTab==='liked') endpoint='/api/users/me/liked';
      if(activeTab==='history') endpoint='/api/users/me/history';
      if(activeTab==='saved') endpoint=`/api/users/me/saved${selectedCollection?`?collection_id=${encodeURIComponent(selectedCollection)}`:''}`;
      const response = await authFetch(`${BACKEND_URL}${endpoint}`);
      if (!response.ok) throw new Error(`Posts request failed: ${response.status}`);
      const data = await response.json();
      if(activeTab==='followers'||activeTab==='following') setPeople(Array.isArray(data)?data:[]);
      else setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!isOwn) return;
    authFetch(`${BACKEND_URL}/api/collections`).then(r=>r.ok?r.json():[]).then(setCollections).catch(()=>{});
  }, [isOwn, authFetch]);

  useEffect(() => { if(activeTab==='saved') loadTab(); }, [selectedCollection]);

  const handleFollow = async () => {
    const newFollowing = !following;
    setFollowing(newFollowing);
    setProfile(p => ({ ...p, followers_count: newFollowing ? p.followers_count + 1 : p.followers_count - 1 }));
    try {
      await authFetch(`${BACKEND_URL}/api/users/${userId}/follow`, { method: 'POST' });
    } catch (err) {
      setFollowing(!newFollowing);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true); setProfileError('');
    try {
      const params = new URLSearchParams({ name: editName, bio: editBio, handle: editHandle });
      const response = await authFetch(`${BACKEND_URL}/api/users/profile?${params}`, {
        method: 'PUT'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Não foi possível salvar o perfil');
      setProfile(p => ({ ...p, ...data }));
      onProfileUpdated?.(data);
      setEditMode(false);
    } catch (err) {
      setProfileError(err.message);
    } finally { setSaving(false); }
  };

  const handleModeratorBan = async () => {
    const reason=window.prompt(`Motivo do banimento permanente de @${profile.handle}:`,'Violação grave ou reincidente das regras')||'';
    if(reason.trim().length<3)return;
    if(!window.confirm('Banir permanentemente esta conta e encerrar todas as sessões?'))return;
    setProfileError('');
    const response=await authFetch(`${BACKEND_URL}/api/moderation/users/${profile.user_id}/ban?reason=${encodeURIComponent(reason.trim())}`,{method:'POST'});
    const data=await response.json().catch(()=>({}));
    if(!response.ok)return setProfileError(data.detail||'Não foi possível banir a conta.');
    window.dispatchEvent(new CustomEvent('blop:notice',{detail:'Conta banida e sessões encerradas.'}));
    onClose();
  };

  const uploadProfileImage = async (kind, file) => {
    setSaving(true); setProfileError('');
    try {
      const body=new FormData(); body.append('file',file);
      const response=await authFetch(`${BACKEND_URL}/api/users/profile/${kind}`,{method:'POST',body});
      const data=await response.json();
      if (!response.ok) throw new Error(data.detail || 'Não foi possível enviar a imagem');
      setProfile(p => ({...p,...data})); onProfileUpdated?.(data);
    } catch(err) { setProfileError(err.message); }
    finally { setSaving(false); }
  };

  const chooseProfileImage = (kind, event) => {
    const file=event.target.files?.[0];
    event.target.value='';
    if (file) setCropRequest({kind,file});
  };

  const visiblePosts = activeTab === 'videos'
    ? posts.filter((post) => post.media_type === 'video')
    : posts;

  return (
    <div
      className="profile-page fixed inset-0 z-[250] flex items-center justify-center"
      style={{ background: '#050506' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="profile-modal"
    >
      <div 
        className="profile-modal-card overflow-y-auto animate-scale-in"
        style={{ background: 'var(--card-bg)', border: '1px solid var(--glass-border)' }}
      >
        {loading || !profile ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{borderColor:'var(--accent)', borderTopColor:'transparent'}}></div>
          </div>
        ) : (
          <>
            {/* Hero */}
            <div
              className="profile-cover relative px-6 border-b"
              style={{ 
                backgroundImage: profile.cover_picture
                  ? `linear-gradient(to bottom,rgba(8,5,7,.2),var(--card-bg)),url("${getMediaUrl(profile.cover_picture)}")`
                  : 'linear-gradient(135deg,#080708 0%,#171014 55%,#2b1914 100%)',
                borderBottomColor: 'var(--glass-border)'
              }}
            >
              <button
                onClick={onClose}
                data-testid="profile-close-btn"
                className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: 'var(--glass)', border: '1px solid var(--glass-border)' }}
              >
                <i className="ti ti-x"></i>
              </button>
              {!isOwn&&<button onClick={()=>setReportOpen(true)} className="absolute top-4 right-14 w-8 h-8 rounded-full flex items-center justify-center" style={{background:'var(--glass)',border:'1px solid var(--glass-border)'}} aria-label="Denunciar perfil"><i className="ti ti-flag"></i></button>}
              {!isOwn&&['moderator','admin'].includes(currentUser?.role)&&<button onClick={handleModeratorBan} className="absolute top-4 right-24 w-8 h-8 rounded-full flex items-center justify-center text-red-300" style={{background:'rgba(127,29,29,.25)',border:'1px solid rgba(251,113,133,.35)'}} aria-label="Banir conta"><i className="ti ti-user-cancel"></i></button>}

              {isOwn && editMode && <label className="profile-cover-action">
                <i className="ti ti-photo-edit"></i>{saving?'Enviando...':'Trocar capa'}
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={event=>chooseProfileImage('cover',event)} className="hidden" disabled={saving}/>
              </label>}

              <div className="profile-identity">
              <div 
                className="profile-main-avatar flex items-center justify-center overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.1)', border: '2px solid var(--glass-border)' }}
                data-testid="profile-avatar"
              >
                {profile.picture ? (
                  <img src={userPictureUrl(profile.picture, getMediaUrl)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <i className="ti ti-user text-4xl text-white/50"></i>
                )}
              </div>

              {editMode ? (
                <div className="profile-edit-fields space-y-2 max-w-md">
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer" style={{background:'var(--glass)',border:'1px solid var(--glass-border)'}}>
                    <i className="ti ti-camera"></i>{saving ? 'Enviando...' : 'Trocar foto'}
                    <input type="file" accept="image/png,image/jpeg,image/webp" onChange={event=>chooseProfileImage('avatar',event)} className="hidden" disabled={saving} />
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    data-testid="edit-name-input"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-center text-lg font-bold focus:outline-none focus:border-white/30"
                  />
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-white/40">@</span>
                    <input type="text" value={editHandle} onChange={(e) => setEditHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,''))} maxLength={32}
                      className="w-full bg-white/5 border border-white/10 rounded-lg pl-7 pr-3 py-2 text-center text-sm focus:outline-none focus:border-white/30" />
                  </div>
                  <textarea
                    value={editBio}
                    onChange={(e) => setEditBio(e.target.value)}
                    data-testid="edit-bio-input"
                    placeholder="Escreva algo sobre você..."
                    rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/70 text-center resize-none focus:outline-none focus:border-white/30"
                  />
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={handleSaveProfile}
                      data-testid="edit-save-btn"
                      className="px-4 py-1.5 rounded-lg text-xs font-semibold"
                      style={{ background: 'var(--accent)' }}
                    >
                      {saving ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button
                      onClick={() => { setEditMode(false); setEditName(profile.name); setEditBio(profile.bio || ''); }}
                      className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white/70"
                      style={{ background: 'var(--glass)' }}
                    >
                      Cancelar
                    </button>
                  </div>
                  {profileError && <p className="text-xs text-red-300">{profileError}</p>}
                </div>
              ) : (
                <div className="profile-copy">
                  <div className="text-2xl font-bold mb-1" data-testid="profile-name">{profile.name}</div>
                  <div className="text-sm mb-3" style={{color:'var(--accent)'}} data-testid="profile-handle">@{profile.handle}</div>
                  <p className="text-sm text-white/60 max-w-xl mb-3 leading-relaxed" data-testid="profile-bio">
                    {profile.bio || 'Membro da comunidade BlopBoru.'}
                  </p>

                  {isOwn ? (
                    <div className="profile-owner-actions">
                      <button onClick={() => setEditMode(true)} data-testid="edit-profile-btn" aria-label="Editar perfil"><i className="ti ti-pencil"></i></button>
                      <button onClick={onOpenSettings} aria-label="Abrir configurações"><i className="ti ti-settings"></i></button>
                    </div>
                  ) : (
                    <button
                      onClick={handleFollow}
                      data-testid="follow-btn"
                      className="px-6 py-2 rounded-lg text-xs font-semibold text-white transition-all active:scale-95"
                      style={{ background: following ? 'var(--glass)' : 'var(--accent)', border: '1px solid var(--glass-border)' }}
                    >
                      <i className={`ti ${following ? 'ti-user-check' : 'ti-user-plus'} mr-1.5`}></i>
                      {following ? 'Seguindo' : 'Seguir'}
                    </button>
                  )}
                </div>
              )}
              </div>

              <div className="profile-primary-stats mt-5 pt-4 border-t border-white/5">
                <button onClick={()=>setActiveTab('followers')} className={activeTab==='followers'?'active':''}><i className="ti ti-users text-sky-300"></i><b>{profile.followers_count}</b><span>Seguidores</span></button>
                <button onClick={()=>setActiveTab('following')} className={activeTab==='following'?'active':''}><i className="ti ti-user-check text-emerald-300"></i><b>{profile.following_count}</b><span>Seguindo</span></button>
                <div><i className="ti ti-heart-filled text-red-400"></i><b>{profile.likes_received_count||0}</b><span>Curtidas</span></div>
              </div>
            </div>

            {/* Profile tabs */}
            <div className="p-5">
              <nav className="profile-content-tabs" aria-label="Conteúdo do perfil">
                <button className={activeTab==='posts'?'active':''} onClick={()=>setActiveTab('posts')}><i className="ti ti-layout-grid"></i><span>Postados</span></button>
                <button className={activeTab==='videos'?'active':''} onClick={()=>setActiveTab('videos')}><i className="ti ti-player-play"></i><span>Vídeos</span></button>
                {isOwn&&<button className={activeTab==='liked'?'active':''} onClick={()=>setActiveTab('liked')}><i className="ti ti-heart"></i><span>Curtidos</span></button>}
                {isOwn&&<button className={activeTab==='saved'?'active':''} onClick={()=>setActiveTab('saved')}><i className="ti ti-bookmark"></i><span>Salvos</span></button>}
              </nav>
              <div className="profile-section-heading"><div className="text-xs font-semibold uppercase text-white/40 tracking-wider">
                {activeTab==='posts'?'Publicações':activeTab==='videos'?'Vídeos':activeTab==='liked'?'Curtidos':activeTab==='saved'?'Coleções salvas':activeTab==='followers'?'Seguidores':'Seguindo'}
              </div>{(activeTab==='posts'||activeTab==='videos')&&<span>{visiblePosts.length} obras</span>}</div>
              {activeTab==='saved' && <div className="profile-collection-filters">
                <button className={!selectedCollection?'active':''} onClick={()=>setSelectedCollection('')}>Tudo</button>
                {collections.map(c=><button className={selectedCollection===c.collection_id?'active':''} key={c.collection_id} onClick={()=>setSelectedCollection(c.collection_id)}>{c.name} <small>{c.items_count}</small></button>)}
              </div>}
              {(activeTab==='followers'||activeTab==='following') ? (
                <div className="space-y-2">{people.length===0?<p className="text-sm text-white/40 py-6 text-center">Nenhum usuário aqui ainda.</p>:people.map(person=><button key={person.user_id} onClick={()=>{onClose();onOpenUser?.(person.user_id);}} className="profile-person-row">
                  {person.picture?<img src={userPictureUrl(person.picture,getMediaUrl)} alt=""/>:<span><i className="ti ti-user"></i></span>}
                  <span><b>{person.name}</b><small>@{person.handle}</small></span><i className="ti ti-chevron-right ml-auto"></i>
                </button>)}</div>
              ) : visiblePosts.length === 0 ? (
                <div className="profile-media-grid">
                  {[0,1,2].map(i => (
                    <div key={i} className="aspect-square rounded-lg flex items-center justify-center text-white/15" style={{background: 'rgba(255,255,255,0.02)', border: '1px dashed var(--glass-border)'}}>
                      <i className="ti ti-layout-grid-add text-lg"></i>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="profile-media-grid">
                  {(Array.isArray(visiblePosts) ? visiblePosts : []).slice(0,30).map(p => (
                    <button
                      key={p.post_id}
                      className="profile-media-tile"
                      onClick={() => { onClose(); onPostClick(p); }}
                      data-testid={`profile-post-${p.post_id}`}
                    >
                      {p.media_type==='3d'?<ModelCardPlaceholder title={p.title} thumbnail={p.thumbnail_url&&p.thumbnail_url!==p.media_url?getMediaUrl(p.thumbnail_url):''}/>:p.media_type==='video'?<video src={getMediaUrl(p.thumbnail_url||p.media_url)} muted playsInline preload="metadata"/>:<img src={getMediaUrl(p.thumbnail_url||p.media_url)} alt={p.title||''}/>} 
                      <span className="profile-media-kind"><i className={`ti ${p.media_type==='video'?'ti-player-play-filled':p.media_type==='3d'?'ti-cube':'ti-photo'}`}></i></span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {reportOpen&&profile&&<ReportModal targetType="user" targetId={profile.user_id} targetLabel={`@${profile.handle}`} onClose={()=>setReportOpen(false)} authFetch={authFetch}/>} 
      {cropRequest&&<ImageCropper file={cropRequest.file} title={cropRequest.kind==='avatar'?'Ajustar foto do perfil':'Ajustar capa do perfil'} aspect={cropRequest.kind==='avatar'?1:16/6} fixedAspect outputWidth={cropRequest.kind==='avatar'?900:1800} onCancel={()=>setCropRequest(null)} onConfirm={file=>{const kind=cropRequest.kind;setCropRequest(null);uploadProfileImage(kind,file);}}/>}
    </div>
  );
}

export default ProfileModal;
